import { useMemo } from 'react';

import { usePermissions } from '@/shared/permissions';

import {
  appSection,
  availableSections,
  firstAvailableSection,
  isSectionPermitted,
} from './appSections';
import type { AppSection, AppSectionKey } from './appSections';
import { useMerchantPortalNavVisibility } from './merchantPortalNavVisibility';

export interface SectionAccess {
  /**
   * May the session's **rights** open this section? What the route guards ask.
   *
   * <p>Deliberately not the same question as "is it in the menu": a workspace staged off for this
   * tenant stays reachable by anyone who holds the permission and has the link, because refusing them
   * would claim they lack a right they have.
   */
  canEnter: (key: AppSectionKey) => boolean;
  /** Every section to list in the sidebar: the session's rights **and** the tenant's staging. */
  sections: AppSection[];
  /**
   * Where to send someone who cannot stay where they are: the first sidebar-visible section they may
   * use. `undefined` when the account can reach nothing, which callers must handle rather than
   * redirect into a loop.
   */
  fallback: AppSection | undefined;
}

/**
 * The session's view of {@link APP_SECTIONS} — the one place the portal asks "where may this user go?".
 *
 * <p>Both consumers read one registry: the sidebar renders {@link SectionAccess.sections}, and every
 * route guard calls {@link SectionAccess.canEnter}. That is the point — the permission behind a menu
 * entry and the permission behind its URL are the same value, not two hand-maintained copies that
 * drifted. The two differ only in whether tenant staging applies, which is a rollout question rather
 * than an access one.
 */
export function useSectionAccess(): SectionAccess {
  const { permissions, hasPermission } = usePermissions();
  const navVisibility = useMerchantPortalNavVisibility();

  return useMemo(() => {
    const context = { permissions, hasPermission };
    return {
      canEnter: (key: AppSectionKey) => isSectionPermitted(appSection(key), context),
      sections: availableSections(context, navVisibility),
      fallback: firstAvailableSection(context, navVisibility),
    };
  }, [permissions, hasPermission, navVisibility]);
}
